import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Persona } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import {
  listPersonas,
  personaKeys,
  suggestPersonas,
  updatePersona,
  updateProjectPersonas,
} from '../api/canvas-personas';
import { isInsufficientCredits } from '../api/client';
import { invalidateCredits } from '../credits/use-credits';
import { logger } from '../lib/logger';
import { usePollWindow } from '../lib/use-poll-window';

const log = logger.child({ module: 'personas' });

/**
 * One project's feedback panel.
 *
 * Personas live in one account-wide pool, but each project has its own panel —
 * so this is keyed per project, and anything that changes a persona (a toggle,
 * a retirement) also marks the pool stale, since the same person may sit on
 * another project's panel too.
 */
export function usePersonas({
  /**
   * Whether a panel is expected to appear on its own.
   *
   * A project's first capture triggers panel generation server-side, and it
   * lands roughly ten seconds later. Without this the panel button says
   * "none" until somebody reloads the page they are already sitting on.
   */
  building = false,
  projectId,
}: { building?: boolean; projectId?: string } = {}) {
  const queryClient = useQueryClient();
  const key = personaKeys.panel(projectId ?? 'none');

  /**
   * Poll only while a panel is expected and none has arrived yet.
   *
   * Computed from the cache rather than from `query.data`, and passed as a
   * plain value — never the predicate form of `refetchInterval`, which is
   * evaluated against query state that has not always caught up with what was
   * just written, so polling silently stops after a couple of ticks. Same trap,
   * and same shape of fix, as the capture list's name polling.
   */
  const cached = queryClient.getQueryData<Persona[]>(key);
  const waiting = Boolean(projectId) && building && cached?.length === 0;
  // Bounded: if the job building the panel died, it is never coming, and the
  // button goes back to "Meet your panel", whose empty modal can suggest one.
  const poll = usePollWindow(waiting);

  const query = useQuery({
    enabled: Boolean(projectId),
    queryFn: () => listPersonas(projectId),
    queryKey: key,
    refetchInterval: poll.refetchInterval,
    staleTime: Infinity,
  });

  const personas = query.data ?? [];

  const write = (next: Persona[]) => {
    queryClient.setQueryData<Persona[]>(key, next);
  };

  const poolIsStale = () => {
    void queryClient.invalidateQueries({ queryKey: personaKeys.pool() });
  };

  /** Toggling in or out of the panel is optimistic — it is a checkbox, and a
   *  checkbox that waits on a round trip reads as broken. */
  const toggle = useMutation({
    mutationFn: ({ active, id }: { active: boolean; id: string }) =>
      updatePersona(id, { active }),
    // Declared before onError: TanStack infers the rollback context from this
    // callback's return type, and an onError written above it sees `{}`.
    onMutate: ({ active, id }) => {
      trackEvent('persona_toggled', { on: active });
      const previous = queryClient.getQueryData<Persona[]>(key);
      write(
        (previous ?? []).map((persona) =>
          persona.id === id ? { ...persona, active } : persona,
        ),
      );
      return { previous };
    },
    onError: (err, _variables, context) => {
      log.error({ err }, 'could not change who is on the panel');
      if (context?.previous) write(context.previous);
    },
    onSettled: poolIsStale,
  });

  /** Retiring somebody. The row survives server-side so past runs still name
   *  who said what; here it simply leaves the list. */
  const retire = useMutation({
    mutationFn: (id: string) => updatePersona(id, { archived: true }),
    onMutate: (id) => {
      const previous = queryClient.getQueryData<Persona[]>(key);
      write((previous ?? []).filter((persona) => persona.id !== id));
      return { previous };
    },
    onError: (err, _id, context) => {
      log.error({ err }, 'could not retire that persona');
      if (context?.previous) write(context.previous);
    },
    onSettled: poolIsStale,
  });

  /** Takes somebody off this project's panel. They stay in the account pool
   *  and on any other project. Optimistic, like the toggle. */
  const removeFromProject = useMutation({
    mutationFn: (id: string) =>
      updateProjectPersonas(projectId!, { remove: [id] }),
    onMutate: (id) => {
      const previous = queryClient.getQueryData<Persona[]>(key);
      trackEvent('persona_removed', {
        personas: Math.max(0, (previous?.length ?? 1) - 1),
      });
      write((previous ?? []).filter((persona) => persona.id !== id));
      return { previous };
    },
    onError: (err, _id, context) => {
      log.error({ err }, 'could not take that persona off the project');
      if (context?.previous) write(context.previous);
    },
    onSuccess: (panel) => {
      write(panel);
    },
    onSettled: poolIsStale,
  });

  /** Brings people from other projects onto this one. Not optimistic: the
   *  server answers with the panel as it now stands. */
  const bring = useMutation({
    mutationFn: (ids: string[]) =>
      updateProjectPersonas(projectId!, { add: ids }),
    onError: (err) => {
      log.error({ err }, 'could not bring those personas onto the project');
      trackEvent('persona_add_failed', { source: 'import' });
    },
    onSuccess: (panel, ids) => {
      trackEvent('persona_added', { count: ids.length, source: 'import' });
      write(panel);
      poolIsStale();
    },
  });

  const suggestMore = useMutation({
    mutationFn: suggestPersonas,
    onError: (err) => {
      log.error({ err }, 'could not suggest more personas');
      if (isInsufficientCredits(err)) {
        trackEvent('insufficient_credits_hit', { feature: 'Suggest personas' });
      }
      trackEvent('persona_add_failed', {
        reason: isInsufficientCredits(err) ? 'credits' : 'error',
        source: 'suggest',
      });
    },
    onSuccess: (added) => {
      invalidateCredits(queryClient);
      trackEvent('persona_added', {
        count: added.length,
        first_panel:
          (queryClient.getQueryData<Persona[]>(key) ?? []).length === 0,
        source: 'suggest',
      });
      queryClient.setQueryData<Persona[]>(key, (previous) => [
        ...(previous ?? []),
        ...added,
      ]);
      poolIsStale();
    },
  });

  return {
    bring: bring.mutate,
    bringing: bring.isPending,
    /** True while this project's first panel is still being written. */
    building: waiting && !poll.expired,
    /** Who a question goes to unless the user says otherwise. */
    selected: personas.filter((persona) => persona.active),
    loaded: query.isSuccess,
    personas,
    removeFromProject: removeFromProject.mutate,
    retire: retire.mutate,
    suggestMore: suggestMore.mutate,
    suggesting: suggestMore.isPending,
    toggle: toggle.mutate,
  };
}

/**
 * Everybody the account has, across projects — what "Add from other projects"
 * picks from. Only fetched while the picker is open.
 */
export function usePersonaPool(enabled: boolean) {
  const query = useQuery({
    enabled,
    queryFn: () => listPersonas(),
    queryKey: personaKeys.pool(),
  });
  return { loading: query.isPending && enabled, pool: query.data ?? [] };
}
